import { MAX_PDF_PAGES } from './constants';
import { OcrError } from './types';

let pdfjsLib: any = null;
// Webpack emits this worker from the installed pdfjs-dist package, keeping its
// version aligned with the dynamically imported PDF.js API.
const PDF_WORKER_SRC = new URL('pdfjs-dist/legacy/build/pdf.worker.min.mjs', import.meta.url).toString();

async function loadPdfJs(): Promise<any> {
  if (pdfjsLib) return pdfjsLib;
  const mod = await import('pdfjs-dist/legacy/build/pdf.mjs');
  const lib = (mod as any).default && (mod as any).getDocument ? mod : (mod as any).default || mod;
  lib.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC;
  pdfjsLib = lib;
  return lib;
}

export interface LoadedPdf {
  doc: any;
  pageCount: number;
  truncated: boolean;
}

export async function loadPdf(file: Blob): Promise<LoadedPdf> {
  const lib = await loadPdfJs();
  let doc: any;
  try {
    const data = new Uint8Array(await file.arrayBuffer());
    doc = await lib.getDocument({ data, isEvalSupported: false, disableAutoFetch: true }).promise;
  } catch (e) {
    throw new OcrError(
      'PDF_PARSE_FAILED',
      'This PDF could not be opened. It may be corrupt or password protected.',
      (e as Error)?.message,
    );
  }
  const total = doc.numPages;
  return { doc, pageCount: Math.min(total, MAX_PDF_PAGES), truncated: total > MAX_PDF_PAGES };
}

/** Extracts embedded (already selectable) text for a page, locally. */
export async function pageEmbeddedText(page: any): Promise<string> {
  try {
    const content = await page.getTextContent();
    const rows = new Map<number, { x: number; str: string }[]>();
    (content.items || []).forEach((item: any) => {
      if (typeof item.str !== 'string' || !item.str.trim()) return;
      const y = Math.round(item.transform[5]);
      const key = Math.round(y / 3) * 3;
      if (!rows.has(key)) rows.set(key, []);
      rows.get(key)!.push({ x: item.transform[4], str: item.str });
    });
    return Array.from(rows.entries())
      .sort((a, b) => b[0] - a[0])
      .map(([, parts]) =>
        parts
          .sort((a, b) => a.x - b.x)
          .map((p) => p.str)
          .join(' ')
          .replace(/\s{2,}/g, ' ')
          .trim(),
      )
      .filter(Boolean)
      .join('\n')
      .trim();
  } catch (e) {
    return '';
  }
}

export function hasUsableEmbeddedText(text: string): boolean {
  return (text.match(/[\p{L}\p{N}]/gu) || []).length >= 20;
}

export interface RenderedPage {
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
}

/** Renders a single page to its own canvas. Caller must release it. */
export async function renderPdfPage(page: any, targetLongSide = 1800): Promise<RenderedPage> {
  const base = page.getViewport({ scale: 1 });
  const scale = Math.min(3, Math.max(1, targetLongSide / Math.max(base.width, base.height)));
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.floor(viewport.width));
  canvas.height = Math.max(1, Math.floor(viewport.height));
  const ctx = canvas.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvasContext: ctx, viewport }).promise;
  return { canvas, width: canvas.width, height: canvas.height };
}
